import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Users, 
  Layers, 
  FileQuestion, 
  MessageSquare,
  Download,
  RefreshCw,
  Database,
  Filter,
  Key,
  Copy,
  Check,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  GitBranch
} from 'lucide-react';
import DatabaseERD from '@/components/DatabaseERD';

// Excel export utility
const exportToExcel = (data, filename) => {
  if (!data || data.length === 0) {
    toast.error('No data to export');
    return;
  }
  
  // Get all unique keys from all objects
  const allKeys = new Set();
  data.forEach(row => Object.keys(row).forEach(key => allKeys.add(key)));
  const headers = Array.from(allKeys);
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  
  toast.success(`Exported ${data.length} rows to ${filename}.csv`);
};

// Data Table component with pagination
const DataTable = ({ data, loading, onExport, title, pageSize = 50 }) => {
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get columns from data
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];
    const allKeys = new Set();
    data.forEach(row => Object.keys(row).forEach(key => allKeys.add(key)));
    return Array.from(allKeys);
  }, [data]);
  
  // Filter data by search
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row => 
      Object.values(row).some(val => 
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);
  
  // Paginate
  const totalPages = Math.ceil((filteredData?.length || 0) / pageSize);
  const paginatedData = filteredData?.slice(page * pageSize, (page + 1) * pageSize) || [];
  
  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);
  
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-xs"
          />
          <Badge variant="secondary">{filteredData?.length || 0} rows</Badge>
        </div>
        <Button onClick={() => onExport(filteredData, title)} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                {columns.map(col => (
                  <th key={col} className="text-left p-2 font-medium text-slate-600 border-b whitespace-nowrap">
                    {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row, i) => (
                <tr key={i} className="border-b hover:bg-slate-50/50">
                  {columns.map(col => (
                    <td key={col} className="p-2 max-w-xs truncate" title={String(row[col] ?? '')}>
                      {row[col] === true ? 'Yes' : row[col] === false ? 'No' : (row[col] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="p-8 text-center text-slate-500">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function DataAnalysisPage() {
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [activeTab, setActiveTab] = useState('erd');
  
  // Data states
  const [usersData, setUsersData] = useState([]);
  const [segmentsData, setSegmentsData] = useState([]);
  const [questionsData, setQuestionsData] = useState([]);
  const [responsesData, setResponsesData] = useState([]);
  
  // Loading states
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  
  // Power BI key
  const [powerbiKey, setPowerbiKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  
  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await api.get('/admin/data-analysis/accounts');
        setAccounts(res.data || []);
      } catch (err) {
        console.error('Failed to fetch accounts:', err);
        toast.error('Failed to load accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };
    fetchAccounts();
    fetchPowerBIKey();
  }, []);
  
  // Fetch projects when account changes
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const params = selectedAccount !== 'all' ? `?account_id=${selectedAccount}` : '';
        const res = await api.get(`/admin/data-analysis/projects${params}`);
        setProjects(res.data || []);
        setSelectedProject('all');
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, [selectedAccount]);
  
  // Fetch data based on filters and active tab
  useEffect(() => {
    fetchData();
  }, [selectedAccount, selectedProject, activeTab]);
  
  const fetchData = async () => {
    // Skip fetching for ERD tab - it's static
    if (activeTab === 'erd') {
      setLoadingData(false);
      return;
    }
    
    setLoadingData(true);
    try {
      const params = new URLSearchParams();
      if (selectedAccount !== 'all') params.append('account_id', selectedAccount);
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      switch (activeTab) {
        case 'users':
          const usersRes = await api.get(`/admin/data-analysis/users${queryString}`);
          setUsersData(usersRes.data || []);
          break;
        case 'segments':
          const segmentsRes = await api.get(`/admin/data-analysis/segments${queryString}`);
          setSegmentsData(segmentsRes.data || []);
          break;
        case 'questions':
          const questionsRes = await api.get(`/admin/data-analysis/questions${queryString}`);
          setQuestionsData(questionsRes.data || []);
          break;
        case 'responses':
          const responsesRes = await api.get(`/admin/data-analysis/responses${queryString}`);
          setResponsesData(responsesRes.data || []);
          break;
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };
  
  const fetchPowerBIKey = async () => {
    try {
      const res = await api.get('/admin/powerbi/get-key');
      setPowerbiKey(res.data?.api_key || null);
    } catch (err) {
      console.error('Failed to fetch Power BI key:', err);
    }
  };
  
  const generatePowerBIKey = async () => {
    try {
      const res = await api.post('/admin/powerbi/generate-key');
      setPowerbiKey(res.data.api_key);
      toast.success('New API key generated');
    } catch (err) {
      toast.error('Failed to generate API key');
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
    toast.success('Copied to clipboard');
  };
  
  const baseUrl = window.location.origin;
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Data Analysis
        </h1>
        <p className="text-slate-600 mt-1">
          Explore and export data for analysis and testing
        </p>
      </div>
      
      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Account:</Label>
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="w-[200px]" data-testid="account-filter">
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Project:</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-[200px]" data-testid="project-filter">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(proj => (
                    <SelectItem key={proj.id} value={proj.id}>{proj.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Data Tables Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="erd" className="flex items-center gap-2" data-testid="tab-erd">
            <GitBranch className="w-4 h-4" />
            Schema
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2" data-testid="tab-users">
            <Users className="w-4 h-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="segments" className="flex items-center gap-2" data-testid="tab-segments">
            <Layers className="w-4 h-4" />
            Segments
          </TabsTrigger>
          <TabsTrigger value="questions" className="flex items-center gap-2" data-testid="tab-questions">
            <FileQuestion className="w-4 h-4" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="responses" className="flex items-center gap-2" data-testid="tab-responses">
            <MessageSquare className="w-4 h-4" />
            Responses
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="erd" className="mt-4">
          <DatabaseERD />
        </TabsContent>
        
        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Users / Stakeholders</CardTitle>
              <CardDescription>All users and stakeholders with their attributes and segment assignments</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                data={usersData} 
                loading={loadingData} 
                onExport={exportToExcel}
                title="users"
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="segments" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Segment Types & Segments</CardTitle>
              <CardDescription>All segment types and their segments defined for each project</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                data={segmentsData} 
                loading={loadingData} 
                onExport={exportToExcel}
                title="segments"
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="questions" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Questions</CardTitle>
              <CardDescription>About Me and About Others questions with their attributes</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                data={questionsData} 
                loading={loadingData} 
                onExport={exportToExcel}
                title="questions"
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="responses" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Responses</CardTitle>
              <CardDescription>About Me and About Others survey responses</CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                data={responsesData} 
                loading={loadingData} 
                onExport={exportToExcel}
                title="responses"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Power BI Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[#5AA2AE]" />
            Power BI Integration
          </CardTitle>
          <CardDescription>
            Connect Microsoft Power BI to this data using the API endpoints below
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API Key */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-slate-500" />
                <Label className="font-medium">API Key</Label>
              </div>
              <Button size="sm" variant="outline" onClick={generatePowerBIKey}>
                {powerbiKey ? 'Regenerate Key' : 'Generate Key'}
              </Button>
            </div>
            {powerbiKey && (
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">
                  {powerbiKey}
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(powerbiKey)}>
                  {keyCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
          
          {/* Endpoints */}
          <div className="space-y-3">
            <Label className="font-medium">API Endpoints (for Power BI Web connector)</Label>
            <p className="text-xs text-slate-500">All endpoints support optional <code className="bg-slate-100 px-1 rounded">account_id</code> and <code className="bg-slate-100 px-1 rounded">project_id</code> query parameters for filtering.</p>
            
            {/* Primary Tables */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-blue-600">Primary Tables</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { name: 'Users', endpoint: '/api/admin/powerbi/users' },
                  { name: 'Organizations', endpoint: '/api/admin/powerbi/organizations' },
                  { name: 'Projects', endpoint: '/api/admin/powerbi/projects' },
                  { name: 'Stakeholders', endpoint: '/api/admin/powerbi/stakeholders' },
                ].map(ep => (
                  <div key={ep.name} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Badge variant="outline" className="w-28 text-xs">{ep.name}</Badge>
                    <code className="flex-1 text-xs font-mono text-slate-600 truncate">
                      {ep.endpoint}
                    </code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => copyToClipboard(`${baseUrl}${ep.endpoint}?api_key=${powerbiKey || 'YOUR_API_KEY'}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Secondary Tables */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-teal-600">Secondary Tables</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { name: 'Segments', endpoint: '/api/admin/powerbi/segments' },
                  { name: 'Segment Types', endpoint: '/api/admin/powerbi/segment-types' },
                  { name: 'Drivers', endpoint: '/api/admin/powerbi/drivers' },
                  { name: 'Questions', endpoint: '/api/admin/powerbi/questions' },
                ].map(ep => (
                  <div key={ep.name} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Badge variant="outline" className="w-28 text-xs">{ep.name}</Badge>
                    <code className="flex-1 text-xs font-mono text-slate-600 truncate">
                      {ep.endpoint}
                    </code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => copyToClipboard(`${baseUrl}${ep.endpoint}?api_key=${powerbiKey || 'YOUR_API_KEY'}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Response Tables */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-rose-600">Response & Transaction Tables</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { name: 'Responses', endpoint: '/api/admin/powerbi/responses' },
                  { name: 'Survey Responses', endpoint: '/api/admin/powerbi/survey-responses' },
                  { name: 'About Others', endpoint: '/api/admin/powerbi/about-others-responses' },
                  { name: 'Selections', endpoint: '/api/admin/powerbi/about-others-selections' },
                  { name: 'Invitations', endpoint: '/api/admin/powerbi/invitations' },
                ].map(ep => (
                  <div key={ep.name} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Badge variant="outline" className="w-28 text-xs">{ep.name}</Badge>
                    <code className="flex-1 text-xs font-mono text-slate-600 truncate">
                      {ep.endpoint}
                    </code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => copyToClipboard(`${baseUrl}${ep.endpoint}?api_key=${powerbiKey || 'YOUR_API_KEY'}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* System Tables */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-slate-600">System Tables</span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { name: 'Subscriptions', endpoint: '/api/admin/powerbi/subscriptions' },
                  { name: 'Pricing Tiers', endpoint: '/api/admin/powerbi/pricing-tiers' },
                  { name: 'Dashboard Configs', endpoint: '/api/admin/powerbi/dashboard-configs' },
                ].map(ep => (
                  <div key={ep.name} className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Badge variant="outline" className="w-28 text-xs">{ep.name}</Badge>
                    <code className="flex-1 text-xs font-mono text-slate-600 truncate">
                      {ep.endpoint}
                    </code>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => copyToClipboard(`${baseUrl}${ep.endpoint}?api_key=${powerbiKey || 'YOUR_API_KEY'}`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800 font-medium mb-2">Power BI Connection Instructions:</p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Generate an API key above (keep it secure)</li>
              <li>In Power BI Desktop, select "Get Data" → "Web"</li>
              <li>Paste the endpoint URL with your API key</li>
              <li>Optional: Add <code className="bg-blue-100 px-1 rounded">account_id</code> or <code className="bg-blue-100 px-1 rounded">project_id</code> query parameters to filter</li>
              <li>Power BI will import the JSON data as a table</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
